import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./login-modern.css";
import myAxios from "../AxiosUrl";
import appIcon from "../../img/appIcon.png";
import { motion, AnimatePresence } from "framer-motion";

function Login() {
  const navigate = useNavigate();
  const [LoginLoading, setLoginLoading] = useState(false);

  // Login fields
  const [lEmail, setLEmail] = useState("");
  const [lpass, setLpass] = useState("");
  // Browser password managers can populate inputs without firing React onChange.
  // Read the DOM values at submit time as a reliable fallback.
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  // Check for login messages from 401 errors
  useEffect(() => {
    const loginMessage = sessionStorage.getItem("loginMessage");
    const loginMessageType = sessionStorage.getItem("loginMessageType");

    if (loginMessage) {
      if (loginMessageType === "warning") {
        toast.warning(loginMessage);
      } else {
        toast.error(loginMessage);
      }

      // Clear the message after showing it
      sessionStorage.removeItem("loginMessage");
      sessionStorage.removeItem("loginMessageType");
    }
  }, []);

  const handleLogin = async () => {
    // Use state for typed values, with DOM values as a fallback for autofill.
    const email =
      lEmail.trim() || emailInputRef.current?.value?.trim() || "";
    const password = lpass || passwordInputRef.current?.value || "";

    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }

    setLEmail(email);
    setLpass(password);
    setLoginLoading(true);
    myAxios
      .post("/login", { email, password })
      .then((res) => {
        setLoginLoading(false);
        if (res.data.success && res.data.token) {
          if (!res.data?.user?.isAdmin) {
            toast.error("This account does not have admin access");
            return;
          }

          const rawAdminType =
            res.data?.user?.AdminType || res.data?.user?.adminType || "";
          const normalizedAdminType = String(rawAdminType)
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/_/g, "");
          const isSuperFromAdminType =
            normalizedAdminType === "superadmin" ||
            normalizedAdminType === "supper" ||
            normalizedAdminType === "super";

          localStorage.setItem("token", res.data.token);
          if (res.data?.user?.id) {
            localStorage.setItem("id", res.data.user.id);
          }
          localStorage.setItem(
            "IsAdmin",
            res.data?.user?.isAdmin ? "true" : "false"
          );
          if (res.data?.user?.isSuperAdmin || isSuperFromAdminType) {
            localStorage.setItem("IsSuper", "true");
          } else {
            localStorage.removeItem("IsSuper");
          }
          if (rawAdminType) {
            localStorage.setItem("AdminType", rawAdminType);
            localStorage.setItem("adminType", rawAdminType);
          } else {
            localStorage.removeItem("AdminType");
            localStorage.removeItem("adminType");
          }

          toast.success("Login successful!");
          navigate("/dashboard");
        } else {
          toast.error(res.data.message || "Login failed");
        }
      })
      .catch((err) => {
        setLoginLoading(false);
        toast.error(err.response?.data?.message || "Login failed");
      });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      // Form onSubmit owns the request so Enter cannot submit twice.
      event.preventDefault();
      handleLogin();
    }
  };

  return (
    <div className="login-modern-container">
      <ToastContainer position="top-center" />
      <div className="login-modern-card">
        <div className="login-modern-header">
          <AnimatePresence mode="wait">
            <motion.img
              key="login-icon"
              src={appIcon}
              alt="App Icon"
              className="login-modern-appicon"
              initial={{ opacity: 0, scale: 0.8, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.4, type: "spring" }}
            />
          </AnimatePresence>
          <AnimatePresence mode="wait">
            <motion.div
              key="login-logo"
              className="login-modern-logo"
              initial={{ opacity: 0, scale: 0.8, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              transition={{ duration: 0.35, type: "spring" }}
            >
              TOPSEKK
            </motion.div>
          </AnimatePresence>
          <div className="login-modern-title">Admin Panel</div>
        </div>
        <div className="login-modern-tabs">
          <button className="login-modern-tab active" type="button">
            Sign In
          </button>
        </div>
        <div>
          <form
            className="login-modern-form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <div className="login-modern-group">
              <label className="login-modern-label" htmlFor="login-email">
                Email
              </label>
              <div className="login-modern-input-wrap">
                <span className="material-icons login-modern-icon">
                  email
                </span>
                <input
                  id="login-email"
                  ref={emailInputRef}
                  type="email"
                  autoComplete="email"
                  className="login-modern-input"
                  placeholder="Enter your email"
                  onChange={(e) => setLEmail(e.target.value)}
                  value={lEmail}
                  onKeyDown={handleKeyPress}
                />
              </div>
            </div>
            <div className="login-modern-group">
              <label className="login-modern-label" htmlFor="login-password">
                Password
              </label>
              <div className="login-modern-input-wrap">
                <span className="material-icons login-modern-icon">lock</span>
                <input
                  id="login-password"
                  ref={passwordInputRef}
                  type="password"
                  autoComplete="current-password"
                  className="login-modern-input"
                  placeholder="Enter your password"
                  onChange={(e) => setLpass(e.target.value)}
                  value={lpass}
                  onKeyDown={handleKeyPress}
                />
              </div>
            </div>
            <button
              className="login-modern-btn"
              type="submit"
              disabled={LoginLoading}
            >
              <span className="material-icons">login</span>
              {LoginLoading ? "Signing In..." : "Sign In"}
            </button>
            <div style={{ textAlign: "center", marginTop: "0.7rem" }}>
              <button
                type="button"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#6b7280",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "14px",
                  padding: 0,
                }}
                onClick={() =>
                  toast.info("Password reset is currently unavailable in admin panel")
                }
              >
                Forgot Password?
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="login-modern-footer">
        &copy; {new Date().getFullYear()} TopSekk. All rights reserved.
      </div>
    </div>
  );
}

export default Login;
